import {
    ArrowLeftRight,
    Landmark,
    Coins,
    Network,
    Sparkles,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBanner from "@/components/CtaBanner";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const VENTURES = [
    {
        icon: ArrowLeftRight,
        name: "DtOrbit Exchange",
        category: "Live",
        live: true,
        desc: "Our core platform for USDT to INR exchange — live rates, secure processing and a simple exchange experience.",
        value: "Key value: a fast, transparent way to convert USDT into Indian Rupees.",
    },
    {
        icon: Landmark,
        name: "Fintech Infrastructure",
        category: "Focus Area",
        live: false,
        desc: "Exploring payment and settlement tooling that makes moving between digital assets and fiat smoother.",
        value: "Key value: reliable financial plumbing for digital asset platforms.",
    },
    {
        icon: Coins,
        name: "Digital Asset Technology",
        category: "Focus Area",
        live: false,
        desc: "Initiatives around digital asset management, exchange technology and rate infrastructure.",
        value: "Key value: technology that keeps exchange rates live and accurate.",
    },
    {
        icon: Network,
        name: "Blockchain & Web3 Solutions",
        category: "Focus Area",
        live: false,
        desc: "Research and development across blockchain networks, transaction processing and Web3 experiences.",
        value: "Key value: dependable on-chain transaction handling.",
    },
];

export default function Ventures() {
    usePageMeta({
        title: "DtOrbit Ventures | Fintech & Digital Asset Initiatives",
        description:
            "Explore DtOrbit Ventures — digital technology and financial technology initiatives built around the DtOrbit USDT to INR exchange ecosystem.",
        path: "/ventures",
    });

    return (
        <>
            <PageHero
                testid="ventures-hero"
                label="Ventures"
                title="DtOrbit"
                highlight="Ventures"
                description="DtOrbit is building and working across digital technology and financial technology initiatives — anchored by our live USDT to INR exchange platform."
            />

            <section data-testid="ventures-list" className="relative py-14 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="max-w-3xl text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                            Each DtOrbit venture is designed to strengthen the
                            bridge between digital assets and everyday finance.
                            Our initiatives are built to be practical, secure
                            and genuinely useful — starting with exchange and
                            expanding into the infrastructure around it.
                        </p>
                    </Reveal>

                    <div className="mt-10 grid sm:grid-cols-2 gap-5 md:gap-6">
                        {VENTURES.map(({ icon: Icon, name, category, live, desc, value }, i) => (
                            <Reveal key={name} delay={(i % 2) * 120}>
                                <div
                                    className="glass card-hover rounded-2xl p-7 h-full flex flex-col"
                                    data-testid={`venture-card-${i + 1}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E56FF] to-[#3EC6F0] shadow-[0_8px_24px_-8px_rgba(30,86,255,0.6)]">
                                            <Icon className="w-5 h-5 text-white" />
                                        </span>
                                        <span
                                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider border ${
                                                live
                                                    ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                                                    : "bg-[#EAF2FF] text-[#1E56FF] border-[#D5E4FF]"
                                            }`}
                                        >
                                            {live && <span className="live-dot" />}
                                            {category}
                                        </span>
                                    </div>
                                    <h3 className="font-display mt-5 text-xl font-bold text-[#0F1B3D]">
                                        {name}
                                    </h3>
                                    <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed flex-1">
                                        {desc}
                                    </p>
                                    <p className="mt-4 text-xs font-semibold text-[#1E56FF]">
                                        {value}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={150}>
                        <div className="mt-10 glass rounded-2xl p-6 md:p-8 flex items-start gap-4" data-testid="ventures-note">
                            <Sparkles className="w-5 h-5 text-[#1E56FF] flex-none mt-0.5" />
                            <p className="text-sm text-[#5B6B8C] leading-relaxed">
                                DtOrbit Ventures is actively evaluating new
                                initiatives across digital finance. Additional
                                ventures will be announced here as they launch.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBanner />
        </>
    );
}
